<script setup lang="ts">
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch, type Ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { UseDraggable } from '@vueuse/components'
import { Maximize2, Mic, MicOff, Pause, Phone, PhoneOff, Play } from 'lucide-vue-next'

import { WebPhone } from '@tmsoft/webphone'
import { useIndexedDB, type ContactsEZdial } from '@/composables/useIndexedDB'
import { useContactService } from '@/services/contacts/useContatcService'

import { useAuthStore } from '@/stores/useAuthStore'
import { useConnectivityStore } from '@/stores/useConnectivityStore'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { closeTrackedSipWebSockets } from '@/helpers/sip-websocket-lifecycle.helper'

const authStore = useAuthStore()
const connectivityStore = useConnectivityStore()
const notificationStore = useNotificationStore()
const workspaceStore = useWorkspaceStore()
const route = useRoute()
const toast = useToast()
const confirm = useConfirm()
const { t } = useI18n()
const { initDB, getAllContactsEZdial } = useIndexedDB()
const { getHubContacts, getExtensionContacts } = useContactService()
const shouldRenderWebphone = computed(
  () =>
    authStore.isAuthenticated &&
    Boolean(authStore.webphoneNumber.trim()) &&
    Boolean(authStore.webphonePassword.trim()),
)
const isSidebarExpanded = inject<Ref<boolean> | null>('isSidebarExpanded', null)
const containerRef = ref<HTMLElement | null>(null)
const webphoneRef = ref<InstanceType<typeof WebPhone> | null>(null)
const lastDialAttemptKey = ref('')
const storedContacts = ref<ContactsEZdial[]>([])
const hubContacts = ref<Array<{ name: string; phone: string }>>([])
const extensionContacts = ref<Array<{ name: string; number: string }>>([])
const googleContacts = ref<Array<{ name: string; phone: string }>>([])
let detachInputSync: (() => void) | null = null
let detachCallStatusSync: (() => void) | null = null
let detachCallStatusPolling: (() => void) | null = null
let detachVisibilityListener: (() => void) | null = null
let detachSubmitGuard: (() => void) | null = null
let detachNavigationGuards: (() => void) | null = null
let lastObservedCallStatus: 'idle' | 'calling' | 'incoming' | 'connected' | 'held' = 'idle'
let lastIncomingNotifiedKey = ''
let unloadHangupTriggered = false
let webphoneReleaseInProgress = false
const isCompactMuted = ref(false)
const isCompactHeld = ref(false)
const isTabHidden = ref(typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false)
const isHomeRoute = computed(() => route.path === '/')
const shouldShowCompactWebphone = computed(
  () => authStore.isWebphoneInCall && !isHomeRoute.value && !authStore.showWebphone,
)
const isWebphoneHidden = computed(() => !authStore.showWebphone)
const enablePictureInPicture = computed(
  () =>
    authStore.isWebphoneInCall &&
    (isTabHidden.value || (!isHomeRoute.value && !authStore.showWebphone)),
)
const webphonePosition = computed(() => ({
  x: isSidebarExpanded?.value ? 236 : 100,
  y: 115,
}))
const compactWebphonePosition = computed(() => {
  if (typeof window === 'undefined') return { x: 24, y: 24 }

  return {
    x: Math.max(window.innerWidth - 380, 16),
    y: Math.max(window.innerHeight - 150, 90),
  }
})

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
const CALL_STATUS_VALUES = new Set(['idle', 'calling', 'incoming', 'connected', 'held'])
const NOTIFICATION_PROMPT_STORAGE_KEY = 'hub.notification.permission.prompted'
const GOOGLE_CONTACT_STORAGE_KEYS = ['googleContacts', 'hub.googleContacts', 'contacts.google']
const RELOAD_BLOCKED_MESSAGE =
  'Estás en una llamada. Si recargas la página, la llamada se finalizará.'
const requestBrowserNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'default') return

  const alreadyPrompted = localStorage.getItem(NOTIFICATION_PROMPT_STORAGE_KEY) === '1'
  if (alreadyPrompted) return

  confirm.require({
    header: 'Permiso de notificaciones',
    message:
      '¿Deseas activar notificaciones del navegador para llamadas entrantes cuando no estés en la app?',
    icon: 'pi pi-bell',
    acceptLabel: 'Permitir',
    rejectLabel: 'Ahora no',
    acceptClass: 'p-button-primary',
    rejectClass: 'p-button-secondary p-button-outlined',
    accept: async () => {
      localStorage.setItem(NOTIFICATION_PROMPT_STORAGE_KEY, '1')
      const permission = await Notification.requestPermission()

      if (permission === 'granted') {
        new Notification('Notificaciones activadas', {
          body: 'Prueba correcta: recibirás alertas de llamadas entrantes.',
          tag: `permission-test-${authStore.webphoneNumber || 'webphone'}`,
        })

        toast.add({
          severity: 'success',
          summary: 'Notificaciones activadas',
          detail: 'Te avisaremos de llamadas entrantes aunque no estés en la app.',
          life: 3500,
        })
        return
      }

      toast.add({
        severity: 'warn',
        summary: 'Notificaciones desactivadas',
        detail: 'Puedes activarlas luego desde la configuración del navegador.',
        life: 4000,
      })
    },
    reject: () => {
      localStorage.setItem(NOTIFICATION_PROMPT_STORAGE_KEY, '1')
    },
  })
}

const getCurrentCallStatusFromDom = () => {
  const root = containerRef.value
  if (!root) return 'idle' as const

  const statusNode = root.querySelector('[data-call-status]') as HTMLElement | null
  if (!statusNode) return 'idle' as const

  const value = (statusNode.getAttribute('data-call-status') || 'idle').trim()
  if (!CALL_STATUS_VALUES.has(value)) return 'idle' as const
  return value as 'idle' | 'calling' | 'incoming' | 'connected' | 'held'
}

const getCurrentRemoteNumberFromDom = () => {
  const root = containerRef.value
  if (!root) return ''

  const selectors = [
    '.webphone-display__non-idle-number',
    '.webphone-display__caller-number',
    '.webphone-display__contact-number',
    '.webphone-display__user-row',
  ]

  for (const selector of selectors) {
    const node = root.querySelector(selector) as HTMLElement | null
    const text = node?.textContent?.trim() || ''
    if (text) return text.replace(/\s+/g, ' ')
  }

  return ''
}

const normalizeDigits = (value?: string | null) => (value ?? '').replace(/\D/g, '')
const isLikelyExtensionNumber = (digits?: string | null) => {
  const normalized = normalizeDigits(digits)
  if (!normalized) return true

  const ownExtension = normalizeDigits(authStore.webphoneNumber)
  if (ownExtension && normalized === ownExtension) return true

  return normalized.length <= 5
}

const extractPhoneNumber = (value?: string | null) => {
  const text = (value ?? '').trim()
  if (!text) return ''

  const candidates = text.match(/\+?\d[\d\s().-]{3,}\d/g) ?? []
  const normalizedCandidates = Array.from(
    new Set(candidates.map((candidate) => normalizeDigits(candidate)).filter(Boolean)),
  )

  const sortedByLength = normalizedCandidates.sort((left, right) => right.length - left.length)
  const bestNonExtension = sortedByLength.find((candidate) => !isLikelyExtensionNumber(candidate))
  if (bestNonExtension) return bestNonExtension

  return sortedByLength[0] ?? normalizeDigits(text)
}

const compactCallTitle = computed(() => {
  const labels = {
    calling: t('common.webphone_status.calling'),
    incoming: t('common.webphone_status.incoming'),
    connected: t('common.webphone_status.connected'),
    held: t('common.webphone_status.held'),
    idle: t('common.titles.webphone'),
  } as const

  return labels[authStore.webphoneCallStatus]
})

const compactCallNumber = computed(() => {
  const candidates = [
    authStore.webphoneRemoteNumber,
    authStore.webphoneTarget,
    workspaceStore.number,
    workspaceStore.lastNumber,
    getCurrentRemoteNumberFromDom(),
  ]
    .map((value) => value?.trim() ?? '')
    .filter(Boolean)

  return candidates.find((value) => !isLikelyExtensionNumber(value)) ?? candidates[0] ?? ''
})

const getAllContactsSource = () => [
  ...storedContacts.value.map((contact) => ({
    name: contact.cont_nombre || contact.cont_telefono,
    number: contact.cont_telefono,
    type: 'contact' as const,
  })),
  ...hubContacts.value.map((contact) => ({
    name: contact.name || contact.phone,
    number: contact.phone,
    type: 'contact' as const,
  })),
  ...extensionContacts.value.map((contact) => ({
    name: contact.name || `Ext. ${contact.number}`,
    number: contact.number,
    type: 'contact' as const,
  })),
  ...googleContacts.value.map((contact) => ({
    name: contact.name || contact.phone,
    number: contact.phone,
    type: 'contact' as const,
  })),
]

const resolveContactNameByExactNumber = (rawNumber?: string | null) => {
  const normalized = normalizeDigits(rawNumber)
  if (!normalized) return ''

  const source = getAllContactsSource()
  for (const item of source) {
    if (normalizeDigits(item.number) !== normalized) continue
    const name = (item.name ?? '').trim()
    if (name && normalizeDigits(name) !== normalized) return name
  }

  return ''
}

const getWebphoneAvailabilityFromDom = () => {
  const root = containerRef.value
  if (!root) return authStore.webphoneAvailable

  const statusTextNode = root.querySelector('.webphone-display__status-text') as HTMLElement | null
  if (!statusTextNode) return authStore.webphoneAvailable

  const classNames = statusTextNode.className || ''
  const isDisconnectedByClass =
    classNames.includes('status-disconnected') ||
    classNames.includes('text-status-disconnected')

  if (isDisconnectedByClass) return false

  const statusText = statusTextNode.textContent?.toLowerCase() ?? ''
  const isUnavailableByText =
    statusText.includes('desconect') ||
    statusText.includes('sin conexión') ||
    statusText.includes('sin conexion')

  return !isUnavailableByText
}

const isWebphoneMutedFromDom = () => {
  const root = containerRef.value
  if (!root) return false

  const muteButtonSelectors = [
    'button.webphone-actions__button--mute',
    'button[aria-label*="Mute"]',
    'button[aria-label*="Unmute"]',
    'button[aria-label*="Silenciar"]',
    'button[aria-label*="Activar sonido"]',
    '[data-action="mute"]',
    '[data-testid*="mute"]',
  ]

  for (const selector of muteButtonSelectors) {
    const button = root.querySelector(selector) as HTMLElement | null
    if (!button) continue

    const ariaPressed = button.getAttribute('aria-pressed')
    if (ariaPressed === 'true') return true

    const className = button.className?.toLowerCase?.() ?? ''
    const title = button.getAttribute('title')?.toLowerCase() ?? ''
    const ariaLabel = button.getAttribute('aria-label')?.toLowerCase() ?? ''
    const dataState = button.getAttribute('data-state')?.toLowerCase() ?? ''

    if (
      className.includes('muted') ||
      className.includes('is-muted') ||
      title.includes('unmute') ||
      title.includes('activar sonido') ||
      ariaLabel.includes('unmute') ||
      ariaLabel.includes('activar sonido') ||
      dataState === 'muted'
    ) {
      return true
    }
  }

  const mediaNodes = root.querySelectorAll('audio, video')
  for (const node of mediaNodes) {
    const media = node as HTMLMediaElement
    if (media.muted || media.volume === 0) return true
  }

  return false
}

const notifyIncomingCall = async () => {
  const remoteRaw = getCurrentRemoteNumberFromDom() || t('common.not_configured')
  const remoteDigits = extractPhoneNumber(remoteRaw)
  const remoteName = resolveContactNameByExactNumber(remoteDigits)
  const remoteDisplay = remoteName || remoteDigits || remoteRaw
  const currentNotifyKey = `${authStore.webphoneNumber}-${remoteDigits || remoteRaw}`
  if (lastIncomingNotifiedKey === currentNotifyKey) return
  lastIncomingNotifiedKey = currentNotifyKey
  notificationStore.addNotification({
    type: 'incoming-call',
    title: 'Llamada entrante',
    message: `Llamada entrante de: ${remoteDisplay}`,
    incomingCallNumber: remoteDigits || remoteRaw,
  })

  toast.add({
    group: 'incoming-call-action',
    severity: 'warn',
    summary: remoteDisplay,
    life: 0,
    closable: false,
  })

  if (typeof window === 'undefined' || !('Notification' in window)) return

  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }

  if (permission !== 'granted') return
  if (isWebphoneMutedFromDom()) return

  const notification = new Notification(t('common.dashboard.incoming'), {
    body: remoteDisplay,
    tag: `incoming-${authStore.webphoneNumber}-${Date.now()}`,
  })

  notification.onclick = () => {
    window.focus()
    authStore.showWebphone = true
  }
}

const clickFirstMatchingButton = (selectors: string[]) => {
  const root = containerRef.value
  if (!root) return false

  for (const selector of selectors) {
    const button = root.querySelector(selector) as HTMLButtonElement | null
    if (!button || button.disabled) continue
    button.click()
    return true
  }

  return false
}

const hangupActiveCall = () => {
  clickFirstMatchingButton([
    'button.webphone-actions__button--hangup',
    'button.webphone-actions__button--reject',
    'button.webphone-actions__button--decline',
    'button.webphone-actions__button--cancel',
    'button[aria-label*="Hang"]',
    'button[aria-label*="Colgar"]',
    'button[aria-label*="Reject"]',
    'button[aria-label*="Rechazar"]',
    'button[aria-label*="Decline"]',
  ])
}

const expandCompactWebphone = () => {
  authStore.showWebphone = true
}

const answerCompactCall = () => {
  clickFirstMatchingButton([
    'button.webphone-actions__button--answer',
    'button.webphone-actions__button--accept',
    'button.webphone-actions__button--call',
    'button[aria-label*="Answer"]',
    'button[aria-label*="Responder"]',
  ])
}

const toggleCompactMute = () => {
  if (
    clickFirstMatchingButton([
      'button.webphone-actions__button--mute',
      'button[aria-label*="Mute"]',
      'button[aria-label*="Silenciar"]',
      '[data-action="mute"]',
    ])
  ) {
    isCompactMuted.value = !isCompactMuted.value
  }
}

const toggleCompactHold = () => {
  if (
    clickFirstMatchingButton([
      'button.webphone-actions__button--hold',
      'button[aria-label*="Hold"]',
      'button[aria-label*="Espera"]',
      'button[aria-label*="Pausar"]',
      '[data-action="hold"]',
    ])
  ) {
    isCompactHeld.value = !isCompactHeld.value
  }
}

const hangupCompactCall = () => {
  hangupActiveCall()
}

const stopWebphoneMediaTracks = () => {
  const root = containerRef.value
  if (!root) return

  root.querySelectorAll('audio, video').forEach((node) => {
    const media = node as HTMLMediaElement
    const stream = media.srcObject instanceof MediaStream ? media.srcObject : null
    stream?.getTracks().forEach((track) => track.stop())
    media.pause()
    media.srcObject = null
  })
}

const resetLocalCallState = () => {
  lastObservedCallStatus = 'idle'
  lastIncomingNotifiedKey = ''
  toast.removeGroup('incoming-call-action')
  authStore.setWebphoneCallStatus('idle')
  authStore.setWebphoneRemoteNumber(null)
  connectivityStore.setSipConnected(true)
}

const releaseTelephonyServices = async () => {
  if (webphoneReleaseInProgress) return

  webphoneReleaseInProgress = true
  try {
    if (authStore.isWebphoneInCall) {
      hangupActiveCall()
      await nextTick()
      await wait(80)
    }

    await webphoneRef.value?.stop()
  } finally {
    stopWebphoneMediaTracks()
    closeTrackedSipWebSockets()
    resetLocalCallState()
    webphoneReleaseInProgress = false
  }
}

const releaseTelephonyServicesBeforePageExit = () => {
  if (unloadHangupTriggered) return

  unloadHangupTriggered = true
  if (authStore.isWebphoneInCall) {
    hangupActiveCall()
  }
  stopWebphoneMediaTracks()
  closeTrackedSipWebSockets()
  resetLocalCallState()
}

const triggerIncomingCallAction = async (action: 'accept' | 'reject') => {
  await nextTick()
  await wait(80)

  const success =
    action === 'accept'
      ? clickFirstMatchingButton([
          'button.webphone-actions__button--answer',
          'button.webphone-actions__button--accept',
          'button[aria-label*="Answer"]',
          'button[aria-label*="Responder"]',
        ])
      : clickFirstMatchingButton([
          'button.webphone-actions__button--reject',
          'button.webphone-actions__button--decline',
          'button.webphone-actions__button--hangup',
          'button[aria-label*="Reject"]',
          'button[aria-label*="Decline"]',
          'button[aria-label*="Hang"]',
          'button[aria-label*="Rechazar"]',
          'button[aria-label*="Colgar"]',
        ])

  if (!success) {
    toast.add({
      severity: 'warn',
      summary:
        action === 'accept'
          ? 'No se pudo aceptar la llamada desde la notificación'
          : 'No se pudo rechazar la llamada desde la notificación',
      life: 2500,
    })
  }
}

const applyCallStatusFromDom = async () => {
  // When the widget is intentionally hidden, avoid downgrading availability from stale/non-visible DOM.
  if (authStore.showWebphone || authStore.isWebphoneInCall) {
    const isWebphoneAvailable = getWebphoneAvailabilityFromDom()
    authStore.setWebphoneAvailability(isWebphoneAvailable)
    connectivityStore.setSipConnected(isWebphoneAvailable)
  }

  const nextStatus = getCurrentCallStatusFromDom()
  if (nextStatus !== 'idle') {
    const liveRemoteNumber = extractPhoneNumber(getCurrentRemoteNumberFromDom())
    if (liveRemoteNumber && !isLikelyExtensionNumber(liveRemoteNumber)) {
      workspaceStore.setCurrentNumber(liveRemoteNumber)
    }
  }

  const prevStatus = lastObservedCallStatus
  const remoteNumber = getCurrentRemoteNumberFromDom().trim()
  const remoteDigits = extractPhoneNumber(remoteNumber)

  if (nextStatus !== 'idle' && remoteNumber && remoteDigits && !isLikelyExtensionNumber(remoteDigits)) {
    authStore.setWebphoneRemoteNumber(remoteNumber)
  }

  if (nextStatus === prevStatus) return

  lastObservedCallStatus = nextStatus
  authStore.setWebphoneCallStatus(nextStatus)

  if (nextStatus === 'incoming') {
    authStore.showWebphone = true
    await notifyIncomingCall()
    return
  }

  // Hide action toast as soon as the call is no longer ringing (accepted/rejected elsewhere).
  toast.removeGroup('incoming-call-action')

  if (nextStatus === 'connected') {
    return
  }

  if (nextStatus === 'idle') {
    lastIncomingNotifiedKey = ''
    isCompactMuted.value = false
    isCompactHeld.value = false
    toast.removeGroup('incoming-call-action')
  }
}

const syncTargetToWebphone = async (target: string) => {
  await nextTick()
  await wait(120)

  const root = containerRef.value
  if (!root) return

  const input = root.querySelector('input.webphone-display__input') as HTMLInputElement | null
  if (!input) return

  input.focus()
  input.value = target
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))

  await wait(30)

  const callButton = root.querySelector(
    'button.webphone-actions__button--call',
  ) as HTMLButtonElement | null

  if (callButton && !callButton.disabled) {
    callButton.click()
  }
}

const bindWebphoneInputSync = async () => {
  await nextTick()
  await wait(120)

  const root = containerRef.value
  if (!root) return

  const input = root.querySelector('input.webphone-display__input') as HTMLInputElement | null
  if (!input) return

  const syncWorkspaceNumber = () => {
    const currentNumber = input.value?.trim() ?? ''
    workspaceStore.setCurrentNumber(currentNumber)
  }

  syncWorkspaceNumber()
  input.addEventListener('input', syncWorkspaceNumber)
  input.addEventListener('change', syncWorkspaceNumber)
  input.addEventListener('blur', syncWorkspaceNumber)

  detachInputSync = () => {
    input.removeEventListener('input', syncWorkspaceNumber)
    input.removeEventListener('change', syncWorkspaceNumber)
    input.removeEventListener('blur', syncWorkspaceNumber)
  }
}

const bindWebphoneCallStatusSync = async () => {
  await nextTick()
  await wait(120)

  const root = containerRef.value
  if (!root) return

  const statusNode = root.querySelector('[data-call-status]') as HTMLElement | null
  if (!statusNode) return

  await applyCallStatusFromDom()

  const observer = new MutationObserver(() => {
    void applyCallStatusFromDom()
  })

  observer.observe(statusNode, {
    attributes: true,
    attributeFilter: ['data-call-status'],
  })

  detachCallStatusSync = () => observer.disconnect()
}

const bindWebphoneCallStatusPolling = () => {
  const intervalId = window.setInterval(() => {
    void applyCallStatusFromDom()
  }, 1000)

  detachCallStatusPolling = () => window.clearInterval(intervalId)
}

const bindPreventSubmitReload = async () => {
  await nextTick()
  await wait(120)

  const root = containerRef.value
  if (!root) return

  const preventSubmitReload = (event: Event) => {
    event.preventDefault()
  }

  root.addEventListener('submit', preventSubmitReload, true)
  detachSubmitGuard = () => {
    root.removeEventListener('submit', preventSubmitReload, true)
  }
}

const bindNavigationReloadGuards = () => {
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!authStore.isWebphoneInCall) return
    event.preventDefault()
    event.returnValue = RELOAD_BLOCKED_MESSAGE
  }

  const handlePageExit = () => {
    releaseTelephonyServicesBeforePageExit()
  }

  const handleReloadShortcut = (event: KeyboardEvent) => {
    if (!authStore.isWebphoneInCall) return

    const isF5 = event.key === 'F5'
    const normalizedKey = event.key.toLowerCase()
    const isRefreshShortcut = (event.ctrlKey || event.metaKey) && normalizedKey === 'r'
    if (!isF5 && !isRefreshShortcut) return

    event.preventDefault()
    event.stopPropagation()
    toast.add({
      severity: 'warn',
      summary: RELOAD_BLOCKED_MESSAGE,
      life: 3200,
    })
  }

  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('pagehide', handlePageExit)
  window.addEventListener('unload', handlePageExit)
  window.addEventListener('keydown', handleReloadShortcut, true)

  detachNavigationGuards = () => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    window.removeEventListener('pagehide', handlePageExit)
    window.removeEventListener('unload', handlePageExit)
    window.removeEventListener('keydown', handleReloadShortcut, true)
  }
}

watch(
  () => [authStore.showWebphone, authStore.webphoneDialToken, authStore.webphoneTarget] as const,
  async ([isOpen, dialToken, target]) => {
    const normalizedTarget = target?.trim()
    if (!isOpen || !normalizedTarget) return

    const dialKey = `${dialToken}:${normalizedTarget}`
    if (lastDialAttemptKey.value === dialKey) return

    lastDialAttemptKey.value = dialKey
    await syncTargetToWebphone(normalizedTarget)
  },
  { flush: 'post' },
)

watch(
  () => authStore.webphoneInstanceKey,
  async () => {
    detachInputSync?.()
    detachInputSync = null
    detachCallStatusSync?.()
    detachCallStatusSync = null
    detachCallStatusPolling?.()
    detachCallStatusPolling = null
    detachSubmitGuard?.()
    detachSubmitGuard = null

    await bindPreventSubmitReload()
    await bindWebphoneInputSync()
    await bindWebphoneCallStatusSync()
    bindWebphoneCallStatusPolling()
  },
  { flush: 'post' },
)

const webphoneContacts = computed(() => {
  const target = authStore.webphoneTarget.trim()
  const normalizedTarget = normalizeDigits(target)

  const allContacts = getAllContactsSource()

  const dedupedByNumber = new Map<string, (typeof allContacts)[number]>()
  for (const item of allContacts) {
    const normalizedNumber = normalizeDigits(item.number)
    if (!normalizedNumber) continue
    const current = dedupedByNumber.get(normalizedNumber)
    const itemName = item.name?.trim() ?? ''
    const currentName = current?.name?.trim() ?? ''

    if (!current) {
      dedupedByNumber.set(normalizedNumber, item)
      continue
    }

    // Prefer contacts with a real name over placeholder-like values.
    const currentLooksPlaceholder = !currentName || currentName === current.number
    const itemLooksBetter = !!itemName && itemName !== item.number

    if (currentLooksPlaceholder && itemLooksBetter) {
      dedupedByNumber.set(normalizedNumber, item)
    }
  }

  const source = Array.from(dedupedByNumber.values()).sort((left, right) =>
    (left.name || '').localeCompare(right.name || '', 'es', { sensitivity: 'base' }),
  )

  const isNumberSearch = normalizedTarget.length > 0
  const lowerTarget = target.toLowerCase()
  const mapped = source.filter((contact) => {
    const phone = normalizeDigits(contact.number)
    const name = contact.name?.toLowerCase() ?? ''
    if (!target) return true
    if (isNumberSearch) return phone === normalizedTarget
    return name.includes(lowerTarget)
  })

  const hasExactMatch = source.some((contact) => normalizeDigits(contact.number) === normalizedTarget)
  if (target && !hasExactMatch) {
    mapped.unshift({
      name: target,
      number: target,
      type: 'contact' as const,
    })
  }

  return mapped
})

const loadGoogleContactsFromStorage = () => {
  for (const key of GOOGLE_CONTACT_STORAGE_KEYS) {
    const raw = localStorage.getItem(key)
    if (!raw) continue

    try {
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) continue

      googleContacts.value = parsed
        .map((item) => ({
          name: String(item?.name ?? '').trim(),
          phone: String(item?.phone ?? item?.number ?? '').trim(),
        }))
        .filter((item) => item.phone)
      return
    } catch {
      continue
    }
  }

  googleContacts.value = []
}

const loadStoredContacts = async () => {
  try {
    await initDB()
    storedContacts.value = await getAllContactsEZdial()
  } catch {
    storedContacts.value = []
  }
}

const loadDirectoryContacts = async () => {
  if (!authStore.isAuthenticated) {
    hubContacts.value = []
    extensionContacts.value = []
    return
  }

  try {
    const [hubResponse, extensionResponse] = await Promise.all([
      getHubContacts(),
      getExtensionContacts(),
    ])

    hubContacts.value = (hubResponse.data ?? []).map((contact) => ({
      name: contact.name,
      phone: contact.phone,
    }))
    extensionContacts.value = (extensionResponse.data ?? []).map((contact) => ({
      name: contact.description || `Ext. ${contact.number}`,
      number: contact.number,
    }))
  } catch {
    hubContacts.value = []
    extensionContacts.value = []
  }
}

const loadContacts = async () => {
  if (!authStore.isAuthenticated) {
    storedContacts.value = []
    hubContacts.value = []
    extensionContacts.value = []
    googleContacts.value = []
    return
  }

  await Promise.all([loadStoredContacts(), loadDirectoryContacts()])
  loadGoogleContactsFromStorage()
}

onMounted(() => {
  requestBrowserNotificationPermission()
  void loadContacts()
  void bindPreventSubmitReload()
  bindNavigationReloadGuards()
  void bindWebphoneInputSync()
  void bindWebphoneCallStatusSync()
  bindWebphoneCallStatusPolling()

  const syncTabVisibility = () => {
    isTabHidden.value = document.visibilityState === 'hidden'

    if (isHomeRoute.value && authStore.isWebphoneInCall) {
      authStore.showWebphone = true
    }

    void applyCallStatusFromDom()
  }

  const onWindowBlur = () => {
    isTabHidden.value = true
  }
  const onWindowFocus = () => {
    syncTabVisibility()
  }

  document.addEventListener('visibilitychange', syncTabVisibility)
  window.addEventListener('blur', onWindowBlur)
  window.addEventListener('focus', onWindowFocus)
  window.addEventListener('pagehide', onWindowBlur)
  window.addEventListener('pageshow', onWindowFocus)

  syncTabVisibility()

  detachVisibilityListener = () => {
    document.removeEventListener('visibilitychange', syncTabVisibility)
    window.removeEventListener('blur', onWindowBlur)
    window.removeEventListener('focus', onWindowFocus)
    window.removeEventListener('pagehide', onWindowBlur)
    window.removeEventListener('pageshow', onWindowFocus)
  }

})

onUnmounted(() => {
  detachInputSync?.()
  detachInputSync = null
  detachCallStatusSync?.()
  detachCallStatusSync = null
  detachCallStatusPolling?.()
  detachCallStatusPolling = null
  detachVisibilityListener?.()
  detachVisibilityListener = null
  detachSubmitGuard?.()
  detachSubmitGuard = null
  detachNavigationGuards?.()
  detachNavigationGuards = null
  void releaseTelephonyServices()
})

watch(
  () => authStore.extension,
  () => {
    if (!authStore.isAuthenticated) return
    void loadContacts()
  },
  { deep: true },
)

watch(
  () => authStore.webphoneCallStatus,
  (status) => {
    if (status === 'incoming' && isHomeRoute.value && !authStore.showWebphone) {
      authStore.showWebphone = true
    }
  },
)

watch(
  () => authStore.incomingCallActionToken,
  async () => {
    const action = authStore.incomingCallActionType
    if (!action) return
    if (authStore.webphoneCallStatus !== 'incoming') return
    await triggerIncomingCallAction(action)
  },
)
</script>

<template>
  <div>
    <div ref="containerRef" :class="{ 'webphone-hidden': isWebphoneHidden }">
      <WebPhone
        v-if="shouldRenderWebphone"
        ref="webphoneRef"
        :early-media="true"
        :key="authStore.webphoneInstanceKey"
        :host="authStore.webphoneDomain"
        :extension="authStore.webphoneNumber"
        :password="authStore.webphonePassword"
        :to="authStore.webphoneTarget"
        :contacts="webphoneContacts"
        :position="webphonePosition"
        :enablePictureInPicture="enablePictureInPicture"
        mode="video"
      />
    </div>

    <UseDraggable
      v-if="shouldShowCompactWebphone"
      :initial-value="compactWebphonePosition"
      prevent-default
      class="webphone-pip fixed z-[80] w-[min(22rem,calc(100vw-2rem))] rounded-2xl app-glass border border-white/70 dark:border-white/10 p-3.5 shadow-xl text-[var(--p-text-color)]"
    >
      <aside aria-live="polite">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex items-center gap-1.5">
              <span
                class="inline-block h-2 w-2 rounded-full shrink-0"
                :class="{
                  'bg-emerald-500': authStore.webphoneCallStatus === 'connected',
                  'bg-blue-500 animate-pulse': authStore.webphoneCallStatus === 'calling' || authStore.webphoneCallStatus === 'incoming',
                  'bg-amber-500': authStore.webphoneCallStatus === 'held',
                  'bg-surface-400': authStore.webphoneCallStatus === 'idle',
                }"
              />
              <p class="truncate text-[11px] font-semibold uppercase tracking-wider text-surface-600 dark:text-surface-300">
                {{ compactCallTitle }}
              </p>
            </div>
            <p class="truncate text-sm font-semibold text-surface-900 dark:text-surface-100 mt-0.5">
              {{ compactCallNumber || t('common.titles.webphone') }}
            </p>
          </div>

          <button
            type="button"
            class="webphone-pip__icon-button"
            :aria-label="t('common.actions.view')"
            @click="expandCompactWebphone"
          >
            <Maximize2 class="h-4 w-4" />
          </button>
        </div>

        <div class="mt-3 grid grid-cols-3 gap-2">
          <button
            v-if="authStore.webphoneCallStatus === 'incoming'"
            type="button"
            class="webphone-pip__action webphone-pip__action--answer"
            :aria-label="t('common.general.accept')"
            @click="answerCompactCall"
          >
            <Phone class="h-4 w-4" />
          </button>

          <button
            v-if="authStore.webphoneCallStatus !== 'incoming'"
            type="button"
            class="webphone-pip__action"
            :class="{ 'webphone-pip__action--muted': isCompactMuted }"
            aria-label="Silenciar"
            @click="toggleCompactMute"
          >
            <MicOff v-if="isCompactMuted" class="h-4 w-4" />
            <Mic v-else class="h-4 w-4" />
          </button>

          <button
            v-if="authStore.webphoneCallStatus !== 'incoming'"
            type="button"
            class="webphone-pip__action"
            :class="{ 'webphone-pip__action--held': isCompactHeld }"
            aria-label="Espera"
            @click="toggleCompactHold"
          >
            <Play v-if="isCompactHeld" class="h-4 w-4" />
            <Pause v-else class="h-4 w-4" />
          </button>

          <button
            type="button"
            class="webphone-pip__action webphone-pip__action--hangup"
            :class="{ 'col-span-2': authStore.webphoneCallStatus === 'incoming' }"
            :aria-label="t('common.destination_type.terminate_call')"
            @click="hangupCompactCall"
          >
            <PhoneOff class="h-4 w-4" />
          </button>
        </div>
      </aside>
    </UseDraggable>
  </div>
</template>

<style scoped>
.cursor-move {
  cursor: move;
}

.webphone-hidden {
  opacity: 0;
  pointer-events: none;
}

.webphone-pip {
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.webphone-pip:active {
  cursor: grabbing;
}

.webphone-pip__icon-button,
.webphone-pip__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  border: 0;
  cursor: pointer;
  transition:
    transform 120ms ease,
    background-color 120ms ease,
    color 120ms ease;
}

.webphone-pip__icon-button {
  height: 2rem;
  width: 2rem;
  border-radius: 0.625rem;
  color: var(--p-text-muted-color);
}

.webphone-pip__icon-button:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--p-text-color);
}

.webphone-pip__action {
  height: 2.5rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--p-primary-color) 12%, transparent);
  color: var(--p-primary-color);
}

.webphone-pip__action:hover {
  transform: translateY(-1px);
  background: color-mix(in srgb, var(--p-primary-color) 20%, transparent);
}

.webphone-pip__action--answer {
  background: #16a34a;
  color: white;
}

.webphone-pip__action--answer:hover {
  background: #15803d;
}

.webphone-pip__action--held {
  background: #f59e0b;
  color: white;
}

.webphone-pip__action--held:hover {
  background: #d97706;
}

.webphone-pip__action--muted,
.webphone-pip__action--hangup {
  background: #dc2626;
  color: white;
}

.webphone-pip__action--muted:hover,
.webphone-pip__action--hangup:hover {
  background: #b91c1c;
}

:global(.dark) .webphone-pip__icon-button:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* Operational Glass refinements for the embedded WebPhone component */
:deep(.webphone) {
  z-index: 70 !important;
  border-radius: 1.25rem !important;
  border: 1px solid rgba(255, 255, 255, 0.75) !important;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.08) !important;
  backdrop-filter: blur(14px) saturate(135%) !important;
  -webkit-backdrop-filter: blur(14px) saturate(135%) !important;
}

:global(.dark .webphone) {
  border: 1px solid rgba(161, 156, 156, 0.192) !important;
  background: rgba(20, 24, 34, 0.88) !important;
  box-shadow: 0 8px 30px -4px rgba(0, 0, 0, 0.4) !important;
}

/* Contact suggestions shown above the dialer input */
:global(.dark .webphone-display__suggestions) {
  color: rgb(226, 232, 240) !important;
  background: rgba(20, 24, 34, 0.98) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 12px 28px -8px rgba(0, 0, 0, 0.55) !important;
}

:global(.dark .webphone-display__suggestion) {
  color: rgb(226, 232, 240) !important;
}

:global(.dark .webphone-display__suggestion:hover) {
  background: rgba(255, 255, 255, 0.08) !important;
}

:global(.dark .webphone-display__contact-name),
:global(.dark .webphone-display__contact-number) {
  color: rgb(226, 232, 240) !important;
}

:global(.dark .webphone-display__contact-number) {
  color: rgb(148, 163, 184) !important;
}

:deep(.webphone-keypad__key) {
  border-radius: 0.75rem !important;
  font-weight: 500 !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  background-color: rgba(0, 0, 0, 0.03) !important;
  transition: background-color 120ms ease, color 120ms ease, transform 120ms ease !important;
}

:global(.dark .webphone-keypad__key) {
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  background-color: rgba(255, 255, 255, 0.04) !important;
}

:deep(.webphone-keypad__key:hover) {
  background-color: color-mix(in srgb, var(--p-primary-color) 12%, transparent) !important;
  color: var(--p-primary-color) !important;
}

:deep(.webphone-actions__button--call) {
  background-image: none !important;
  background-color: var(--p-primary-color) !important;
  border-radius: 9999px !important;
  box-shadow: 0 2px 3px -2px color-mix(in srgb, var(--p-primary-color) 40%, transparent) !important;
}

:deep(.webphone-actions__button--call:hover) {
  background-color: color-mix(in srgb, var(--p-primary-color) 88%, black) !important;
}

:deep(.webphone-actions__button--hangup),
:deep(.webphone-actions__button--reject),
:deep(.webphone-actions__button--cancel) {
  background-image: none !important;
  background-color: #dc2626 !important;
  border-radius: 9999px !important;
}

:deep(.webphone-actions__button--hangup:hover),
:deep(.webphone-actions__button--reject:hover),
:deep(.webphone-actions__button--cancel:hover) {
  background-color: #b91c1c !important;
}

:deep(.webphone-actions__button--answer) {
  background-color: #16a34a !important;
  border-radius: 9999px !important;
}

:deep(.webphone-actions__button--answer:hover) {
  background-color: #15803d !important;
}

:deep(.webphone-actions__button--hold) {
  border-radius: 9999px !important;
}

:deep(.webphone-actions__input) {
  border-radius: 0.75rem !important;
}
</style>
